import React, { memo, useMemo, useRef, useEffect } from 'react'

import ButtonGroupSelector from 'Pods/ThreeDStudio/Shared/ButtonGroupSelector/ButtonGroupSelector'
import PolyCountSlider from
  'Pods/ThreeDStudio/Shared/GenericPolyCountSelector/Components/PolyCountSlider/PolyCountSlider'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setFaceLevel, setTargetPolycount } from 'Pods/ThreeDStudio/Store/actions/remeshSettings'
import { FACE_LEVEL } from 'Pods/ThreeDStudio/Store/constants'

const handleLowClick = () => setFaceLevel(FACE_LEVEL.LOW)
const handleMediumClick = () => setFaceLevel(FACE_LEVEL.MEDIUM)
const handleHighClick = () => setFaceLevel(FACE_LEVEL.HIGH)
const handleTargetPolyCountChange = (value) => setTargetPolycount(value)

function PolyCountSelector() {
  const isDisabled = useThreeDStudioStore(state => {
    const faceLevelSchema = state.schema?.enhancedModelSchema?.ui_schema?.face_level || {}
    return faceLevelSchema?.['ui:options']?.disabled
  })
  const faceLevel = useThreeDStudioStore(state => state.remeshSettings.faceLevel)
  const targetPolyCountSchema = useThreeDStudioStore(
    state => state.schema?.enhancedModelSchema?.input_schema?.properties?.target_polycount
  )
  const isSliderDisabled = !targetPolyCountSchema
  const targetPolycount = useThreeDStudioStore(state => state.remeshSettings.targetPolycount)
  const { minimum, default: defaultValue, maximum } = targetPolyCountSchema || {}

  const lastKnownRange = useRef({ minimum, maximum, targetPolycount })
  if (!isSliderDisabled) {
    lastKnownRange.current = { minimum, maximum, targetPolycount }
  }
  const sliderMinimum = isSliderDisabled ? lastKnownRange.current.minimum : minimum
  const sliderMaximum = isSliderDisabled ? lastKnownRange.current.maximum : maximum
  const sliderTargetPolycount = isSliderDisabled ? lastKnownRange.current.targetPolycount : targetPolycount

  const previousRange = useRef({
    minimum: undefined,
    defaultValue: undefined,
    maximum: undefined,
  })

  useEffect(() => {
    if (!minimum || !maximum || !defaultValue) {
      return
    }

    const {
      minimum: prevMinimum,
      defaultValue: prevDefaultValue,
      maximum: prevMaximum,
    } = previousRange.current
    const rangeChanged = prevMinimum !== minimum
      || prevMaximum !== maximum
      || prevDefaultValue !== defaultValue

    if (rangeChanged) {
      previousRange.current = { minimum, defaultValue, maximum }

      if (targetPolycount === prevMinimum) {
        handleLowClick(true)
        return
      }
      if (targetPolycount === prevMaximum) {
        handleHighClick(true)
        return
      }
      if (targetPolycount === prevDefaultValue) {
        handleMediumClick(true)
        return
      }

      if (targetPolycount < minimum) {
        handleLowClick(true)
      } else if (targetPolycount > maximum) {
        handleHighClick(true)
      }
    }
  }, [minimum, maximum, defaultValue, targetPolycount])

  const buttons = useMemo(() => [
    {
      id: FACE_LEVEL.LOW,
      label: 'Low',
      onClick: handleLowClick,
      disabled: isDisabled,
      isActive: targetPolycount === minimum || (!targetPolyCountSchema && faceLevel === FACE_LEVEL.LOW),
    },
    {
      id: FACE_LEVEL.MEDIUM,
      label: 'Medium',
      onClick: handleMediumClick,
      disabled: isDisabled,
      isActive: targetPolycount === defaultValue || (!targetPolyCountSchema && faceLevel === FACE_LEVEL.MEDIUM),
    },
    {
      id: FACE_LEVEL.HIGH,
      label: 'High',
      onClick: handleHighClick,
      disabled: isDisabled,
      isActive: targetPolycount === maximum || (!targetPolyCountSchema && faceLevel === FACE_LEVEL.HIGH),
    },
  ], [
    targetPolyCountSchema,
    targetPolycount,
    faceLevel,
    isDisabled,
    handleLowClick,
    handleMediumClick,
    handleHighClick,
  ])

  return (
    <>
      <ButtonGroupSelector
        buttons={buttons}
        keyName="polyCount"
        sliceName="remeshSettings"
        label="Polycount"
      />
      <PolyCountSlider
        isDisabled={isSliderDisabled}
        setTargetPolycount={handleTargetPolyCountChange}
        targetPolycount={sliderTargetPolycount}
        minimum={sliderMinimum}
        maximum={sliderMaximum}
      />
    </>
  )
}

export default memo(PolyCountSelector)
